import { BouncyAccordion, type BouncyAccordionItem } from '@/components/ui/bouncy-accordion';
import { Reveal } from '@/components/ui/reveal';
import { JsonLd } from '@/components/seo';

const FAQ_ITEMS = [
  {
    id: 'item-1',
    question: 'When can I expect my first report?',
    answer:
      'Within 7 business days of signing up. A local inspector visits your plot and documents the boundary and condition on site. We then put the photos, video and checklist together into one report and send it to your phone or email, wherever you are.',
  },
  {
    id: 'item-2',
    question: 'Can I sign up if I live outside India?',
    answer:
      'Yes. Boundr is built for NRIs and other remote landowners. Sign-up, monitoring, payments and reports are all online, so you never need to be in India for any of it. Many of our customers manage their plots from the US, UK and the Gulf.',
  },
  {
    id: 'item-3',
    question: 'Do I need to live near my plot to sign up?',
    answer:
      'No. Sign-up, monitoring and reports are all online. Whether you live in another Indian city or abroad, you can manage everything remotely without coordinating visits through relatives or a local contact.',
  },
  {
    id: 'item-4',
    question: 'Can I sign-up even if I live nearby?',
    answer:
      'Yes. Plenty of our customers live in the same city but do not have the time to get out to the plot regularly.',
  },
  {
    id: 'item-5',
    question: 'Can I save or export my inspection report?',
    answer: "Yes. Download it as a PDF, or use your device's print function.",
  },
  {
    id: 'item-6',
    question: 'What are the areas you cover?',
    answer:
      'We cover Greater Hyderabad and Greater Bangalore and their surroundings, along with Guntur, Visakhapatnam and Vijayawada. These are the regions in Telangana, Karnataka and Andhra Pradesh where NRI-owned vacant plots are most common.',
  },
  {
    id: 'item-7',
    question: 'Are you planning to expand to other cities?',
    answer:
      'Yes. We are scoping new cities based on where customers ask for us, starting with other major hubs in South India. If your plot falls outside our current coverage, join the waitlist and we will let you know when we launch in your area.',
  },
  {
    id: 'item-8',
    question: 'How long do I have to change or cancel my subscription?',
    answer:
      'You can cancel any time from your dashboard. Cancellation ends the subscription immediately. If a report for the current paid period has already been completed, that period is non-refundable. If no report has been completed yet for a paid period, you may be eligible for a refund of that unused period. There are no cancellation fees or lock-in for future periods.',
  },
  {
    id: 'item-9',
    question: 'What is encroachment (Kabzaa) and why does it matter?',
    answer:
      'Encroachment, locally called "Kabzaa", is when someone occupies or builds on part of your land without permission. That can mean moved boundary markers, dumped construction debris, or a structure going up. It hits NRI landowners hardest because it usually starts small and goes unnoticed for months or years when nobody is around to catch it. The longer it runs, the harder and costlier it is to reverse legally.',
  },
  {
    id: 'item-10',
    question: 'Can inspection reports be used as legal evidence in Indian courts?',
    answer:
      "Our reports contain timestamped photos, video and GPS-tagged visit records, so they document the state of your property on a given date. Filed with the right authorities or handed to a lawyer, they can support a case around possession or encroachment. They do not replace a surveyor's legal report, but they do create a dated record that strengthens your position in a dispute. Please consult a lawyer for advice on your specific situation.",
  },
  {
    id: 'item-11',
    question: 'What should I do if an inspector finds encroachment on my land?',
    answer:
      "If our inspector flags encroachment, you'll see it in your report right away with photo and video evidence. Act on it quickly: take the report to local authorities or a property lawyer, request a formal survey if one is needed, and use the dated record to establish when the encroachment started. The earlier you move, before any construction is finished, the stronger your footing.",
  },
];

const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: answer,
    },
  })),
};

// FAQ_ITEMS stays the source of truth so the JSON-LD above and the rendered
// accordion can never drift apart.
const ACCORDION_ITEMS: BouncyAccordionItem[] = FAQ_ITEMS.map(({ id, question, answer }) => ({
  id,
  title: question,
  description: answer,
}));

export function Faqs() {
  return (
    <section id="faq" className="border-t border-line-strong bg-card/60 scroll-mt-8">
      <JsonLd id="faq-jsonld" data={FAQ_JSON_LD} />
      <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20 md:py-28">
        <div className="grid gap-10 sm:gap-12 lg:grid-cols-[minmax(0,4fr)_minmax(0,7fr)] lg:gap-20">
          <Reveal className="lg:sticky lg:top-12 lg:self-start">
            <h2 className="font-display text-3xl font-medium leading-tight tracking-tight text-foreground [overflow-wrap:anywhere] md:text-4xl">
              Questions, answered.
            </h2>
            <p className="mt-5 max-w-sm leading-relaxed text-foreground/65">
              Can't find what you're looking for? Contact our{' '}
              <a
                href="/contact-us"
                className="text-ring underline-offset-4 hover:underline focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                customer support
              </a>
              .
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            {/*
             * Rows are `bg-background` against the section's `bg-card/60` band.
             * That keeps the tiles darker than the band in BOTH themes — using
             * `bg-card` would invert the relationship in dark mode.
             */}
            <BouncyAccordion
              items={ACCORDION_ITEMS}
              radius={16}
              classNames={{
                item: 'bg-background',
                trigger: 'px-5 py-4 hover:bg-muted/40',
                title: 'font-display text-base font-medium text-foreground',
                chevron: 'mt-0.5 text-foreground/60',
                description: 'max-w-prose leading-relaxed text-foreground/70',
              }}
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
